import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, onClick, toast, confirmDialog } from '../dom.js';
import { icon } from '../icons.js';
import { studentFormDialog } from '../studentForm.js';

// §8 Student Detail: a derived cross-quiz history for one roster student. Each
// row reuses the existing single-sheet Review view (#/result/:id) — not a second
// detail screen. Captured-name results have no studentId, so they never appear.
export async function studentDetailView(params: Record<string, string>): Promise<RenderedView> {
  const student = await repos.getStudent(params.id);
  if (!student) return { title: 'Student', back: '/students', html: `<div class="view"><p class="empty">Student not found.</p></div>` };
  const [cls, results] = await Promise.all([repos.getClass(student.classId), repos.resultsForStudent(student.id)]);

  const asgs = await Promise.all([...new Set(results.map((r) => r.quizAssignmentId))].map((id) => repos.getAssignment(id)));
  const asgById = new Map(asgs.filter(Boolean).map((a) => [a!.id, a!]));
  const quizzes = await Promise.all([...new Set(asgs.filter(Boolean).map((a) => a!.quizId))].map((id) => repos.getQuiz(id)));
  const quizById = new Map(quizzes.filter(Boolean).map((q) => [q!.id, q!]));

  const rows = results
    .map((r) => {
      const a = asgById.get(r.quizAssignmentId);
      const q = a ? quizById.get(a.quizId) : undefined;
      return { r, q, date: a?.dateAdministered ?? '' };
    })
    .filter((x) => x.q)
    .sort((x, y) => y.date.localeCompare(x.date) || y.r.capturedAt - x.r.capturedAt);

  const list = rows.length
    ? `<ul class="list">${rows
        .map(
          ({ r, q, date }) => `<button class="row" data-result="${esc(r.id)}"><span class="grow"><span class="ttl">${esc(q!.title)}</span>
          <span class="meta">${esc(q!.subject || '—')} · ${esc(date)}</span></span>
          <span class="meta"><span class="mono">${r.score != null ? `${r.score}/${r.maxScore}` : '—'}</span></span><span class="chev">${icon.chevron()}</span></button>`,
        )
        .join('')}</ul>`
    : `<div class="empty">${icon.doc()}<p>No quizzes scored yet.</p></div>`;

  return {
    title: student.name,
    subtitle: `${cls?.name ?? ''}`,
    back: '/students',
    html: `<div class="view">
      <div class="card"><div class="stack">
        <div class="muted" style="font-size:14px">LRN <span class="mono">${esc(student.lrn)}</span>${student.sex ? ` · ${esc(student.sex)}` : ''}</div>
        <div class="row2"><button class="btn btn-ghost btn-sm" data-edit>${icon.edit()} Edit</button>
          <button class="btn btn-danger btn-sm" data-del>${icon.trash()} Delete</button></div>
      </div></div>
      <p class="section-label">Quiz history (${rows.length})</p>
      ${list}
    </div>`,
    mount(root) {
      onClick(root, '[data-result]', (el) => navigate(`#/result/${el.dataset.result}`));
      onClick(root, '[data-edit]', () => studentFormDialog({ existing: student, onDone: () => navigate(location.hash || '#/students') }));
      onClick(root, '[data-del]', async () => {
        if (!confirmDialog(`Delete ${student.name}? Scanned results stay but become unassigned.`)) return;
        await repos.deleteStudent(student.id);
        toast('Student deleted');
        navigate('#/students');
      });
    },
  };
}
